import ModuleCardList from "./module-card-list";
import ListView from "./list-view";
import Formula from "./formula";
import Field from "./field";
import Entity from "./entity";
import DocumentsCache from "./documents-cache";
import Api from "./api";
import { arrayCompare, equal } from "../utils";
import IConfigPhone from "../interface/field-cfg-phone";
import IConfigNumber from "../interface/field-cfg-number";
import IConfigLookup from "../interface/field-cfg-lookup";
import IConfigLinked from "../interface/field-cfg-linked";
import IConfigFormulaRenewal from "../interface/field-cfg-formula-renewal";
import IConfigFormulaNumber from "../interface/field-cfg-formula-number";
import IConfigFormulaDatetime from "../interface/field-cfg-formula-datetime";
import IConfigFormula from "../interface/field-cfg-formula";
import IConfigDatetime from "../interface/field-cfg-datetime";
import ILinkedFieldClientConfig from "../interface/field-cfg-client-linked";
import IFieldConfig from "../interface/field-cfg";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IApiPointer from "../interface/api/api-pointer";
import IApiField from "../interface/api/api-field";
import IApiEntity from "../interface/api/api-entity";
import Global from "../global";
import FormulaType from "../enum/formula-type";
import FieldTypeConfig from "../enum/field-type-config";
import FieldType from "../enum/field-type";
import FieldSystemType from "../enum/field-system-type";
import ErrorCode from "../enum/error-code";
import DefaultConfig from "../default-config";

class FieldPromoteInstance {

    /* Public Methods */

    public async toBasic(fieldId: number, type: FieldType): Promise<IField> {
        const field: IApiField = this.getField(fieldId);
        return await this.updateSimpleType(field, type);
    }

    public async toPhone(fieldId: number, config: IConfigPhone = DefaultConfig[FieldType.phone]()): Promise<IField> {
        const field: IApiField = this.getField(fieldId);
        return await this.updateSimpleType(field, FieldType.phone, config);
    }

    public async toDatetime(fieldId: number, config: IConfigDatetime = DefaultConfig[FieldType.datetime]()): Promise<IField> {
        const field: IApiField = this.getField(fieldId);
        return await this.updateSimpleType(field, FieldType.datetime, config);
    }

    public async toFormula(fieldId: number, config: IConfigFormula): Promise<IField> {
        const field: IApiField = this.getField(fieldId);
        switch (config.type) {
            case FormulaType.number:
                const formulaNum: IConfigFormulaNumber = config.formula as IConfigFormulaNumber;
                formulaNum.expression = Formula.expressionParse(field.eCN, formulaNum.expression);
                config.dependencies = Formula.getDependenciesNumber(formulaNum);
                break;
            case FormulaType.datetime:
                const formulaDat: IConfigFormulaDatetime = config.formula as IConfigFormulaDatetime;
                config.dependencies = Formula.getDependenciesDatetime(formulaDat);
                break;
            case FormulaType.renewal:
                const formulaRen: IConfigFormulaRenewal = config.formula as IConfigFormulaRenewal;
                config.dependencies = Formula.getDependenciesRenewal(formulaRen);
                break;
        }
        return await this.updateSimpleType(field, FieldType.formula, config);
    }

    public async toNumber(fieldId: number, config: IConfigNumber = DefaultConfig[FieldType.number]()): Promise<IField> {
        const field: IApiField = this.getField(fieldId);
        return await this.updateSimpleType(field, FieldType.number, config);
    }

    public async toLinked(fieldId: number, config: ILinkedFieldClientConfig, uiConfig: IConfigLinked = DefaultConfig[FieldType.linked]()): Promise<IField> {
        const field: IApiField = this.getField(fieldId);
        if (field.uiType === FieldType.lookup) {
            /* CRUTCH: Change lookup to linked field is very hard to implement without Nico */
            const stringField = await this.updateSimpleType(field, FieldType.text);
            return await this.toLinked(stringField.id, config, uiConfig);
        } else {
            if (field.uiType !== FieldType.linked) {
                config.verbFrom = config.verbFrom || Global.REL_SUB_MASTER;
                config.verbTo = config.verbTo || Global.REL_SUB_DETAIL;
                /* If creating link to new table */
                if (!config.entityId) {
                    config.singular = (config.singular || '').trim();
                    if (config.singular) {
                        config.entityId = -1;
                        config.fieldId = -1;
                    } else {
                        throw ErrorCode.BadArguments;
                    }
                } else if (!config.fieldId) {
                    config.fieldId = this.selectDefaultEntityFieldId(config.entityId);
                }
            }
            return await this.updateLinkedType(field, FieldType.linked, {
                pointerId: [-1],
                entityId: config.entityId,
                fieldId: config.fieldId,
                verbTo: config.verbTo,
                verbFrom: config.verbFrom,
                singular: config.singular,
                linkedTemplate: config.linkedTemplate
            }, uiConfig);
        }
    }

    public async toLookup(fieldId: number, config: IConfigLookup): Promise<IField> {
        const field: IApiField = this.getField(fieldId);
        /* Validate all pointers in chain */
        let validPointers = true;
        config.pointerId.forEach((pointerId: number) => {
            if (!Field.get(pointerId)) {
                validPointers = false;
            }
        });
        if (config.pointerId.length && validPointers) {
            return await this.updateLinkedType(field, FieldType.lookup, {
                linkedTemplate: config.linkedTemplate,
                pointerId: config.pointerId,
                fieldId: config.fieldId
            });
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Private Methods */

    private async updateSimpleType(field: IApiField, type: FieldType, config: IFieldConfig = {}): Promise<IField> {
        let callSave: boolean = false;
        field = await this.toSimpleType(field, type);
        const systemType: FieldSystemType = this.getSystemType(type);
        if (field.systemType !== systemType) {
            callSave = true;
            field.systemType = systemType;
        }
        if (field.uiType !== type) {
            callSave = true;
            field.uiType = type;
        }
        if (!equal(field.uiConfig, config)) {
            callSave = true;
            field.uiConfig = config;
        }
        if (callSave) {
            await Api.fieldSave(field);
        }
        return Field.get(field.id) as IField;
    }

    private async updateLinkedType(field: IApiField, type: FieldType, config: ILinkedFieldClientConfig, uiConfig: IConfigLinked = DefaultConfig[FieldType.linked]()): Promise<IField> {
        let callSave: boolean = false;
        field = await this.toLinkedType(field, type, config);
        let oldPointers: number[] = [];
        if (config.pointerId && config.pointerId[0] !== -1 && !equal(field.linkedRelation, config.pointerId)) {
            oldPointers = [...field.linkedRelation as number[]];
            field.linkedRelation = config.pointerId;
            callSave = true;
        }
        const linkedTemplate: string = `<%= c${config.fieldId} %>`;
        if (config.fieldId && config.fieldId !== -1 && field.linkedTemplate !== linkedTemplate) {
            field.linkedTemplate = `<%= c${config.fieldId} %>`;
            callSave = true;
        }
        if (field.uiType !== type) {
            field.uiType = type;
            callSave = true;
        }
        if (config.linkedTemplate && config.linkedTemplate !== field.linkedTemplate) {
            field.linkedTemplate = config.linkedTemplate;
            callSave = true;
        }
        if (type === FieldType.linked) {
            const config: IConfigLinked = field.uiConfig as IConfigLinked;
            if (config.forbidRepetitions !== uiConfig.forbidRepetitions || !arrayCompare(config.filters, uiConfig.filters)) {
                field.uiConfig = uiConfig;
                callSave = true;
            }
        }
        if (callSave) {
            try {
                await field.save();
                if (oldPointers.length) {
                    await this.clearPointers(oldPointers);
                }
            } catch (e) {
                throw ErrorCode.ApiInternal;
            }
        }
        return Field.get(field.id) as IField;
    }

    private async toSimpleType(field: IApiField, type: FieldType = FieldType.text): Promise<IApiField> {
        if (this.isLinked(field)) {
            let oldFieldId: number = field.id;
            let newFieldId: number = 0;
            if (field.uiType === FieldType.linked) {
                const result: IApiField = await Api.fieldChangeToString(field, type);
                newFieldId = result.id;
            } else {
                const entity: IApiEntity = Api.getEntity(field.eCN) as IApiEntity;
                const result: IApiField = await Api.fieldCreateString(entity, field.id, field.singular, type);
                newFieldId = result.id;
            }
            field = Api.getField(newFieldId) as IApiField;
            await ListView.replaceFieldConfig(field.eCN, oldFieldId, newFieldId);
        }
        return field;
    }

    private async toLinkedType(field: IApiField, type: FieldType, config: ILinkedFieldClientConfig): Promise<IApiField> {
        if (!this.isLinked(field)) {
            let newEntityId: number = 0;
            let oldFieldId: number = 0;
            let newFieldId: number = 0;
            let pointerId: number = -1;
            let entityId: number = config.entityId || -1;
            pointerId = (config.pointerId as number[]).slice(-1).pop() as number;
            if (pointerId !== -1 && entityId === -1) {
                const ptField: IApiField = Api.getField(pointerId) as IApiField;
                entityId = (ptField.pointer as IApiPointer[])[1].ptEcn;
            }
            let newField: IApiField | null = null;
            if (type === FieldType.linked) {
                oldFieldId = field.id;
                newField = await Api.fieldChangeToLinked(field, entityId, config);
                if (entityId === -1) {
                    newEntityId = newField.foreignerEcn as number;
                }
                newFieldId = newField.id;
            } else {
                const entity: IApiEntity = Api.getEntity(field.eCN) as IApiEntity;
                if (!config.linkedTemplate && config.fieldId) {
                    config.linkedTemplate = `<%= c${config.fieldId} %>`;
                }
                oldFieldId = field.id;
                newField = await Api.fieldCreateLinked(entity, field.id, {
                    linkedTemplate: config.linkedTemplate,
                    linkedRelation: config.pointerId as number[],
                    singular: field.singular,
                    uiType: FieldType.lookup
                });
                newFieldId = newField.id;
            }
            DocumentsCache.dictionaryUpdate((newField as IApiField).eCN);
            field = Api.getField(newFieldId) as IApiField;
            await ListView.replaceFieldConfig(field.eCN, oldFieldId, newFieldId);
            if (newEntityId) {
                await ModuleCardList.createByEntityId(newEntityId);
            }
        }
        return field;
    }

    private getField(fieldId: number): IApiField {
        const field: IApiField | null = Api.getField(fieldId);
        if (field) {
            return field;
        } else {
            throw ErrorCode.NoField;
        }
    }

    private getSystemType(type: FieldType): FieldSystemType {
        const cfg = FieldTypeConfig[type];
        return cfg.systemType;
    }

    private isLinked(field: IApiField): boolean {
        return this.getSystemType(field.uiType) === FieldSystemType.linked
    };

    private selectDefaultEntityFieldId(entityId: number): number {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            const field = entity.fields.find((field: IField) => field.systemType === FieldSystemType.string);
            if (field) {
                return field.id;
            } else {
                return -1;
            }
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    /**
     * Check if pointer doesn't have related linked field - it will be removed
     * @param pointersIds - Pointer field ID
     */
    private async clearPointers(pointersIds: number[]): Promise<void> {
        for (let i = 0; i < pointersIds.length; i++) {
            const pointer: IApiField = Api.getField(pointersIds[i]) as IApiField;
            const entity: IEntity = Entity.get(pointer.eCN) as IEntity;
            let hasLinkedFields = false;
            for (let i = 0; i < entity.fields.length; i++) {
                const field = entity.fields[i];
                if (field.linkedRelation && field.linkedRelation.indexOf(pointer.id) > -1) {
                    hasLinkedFields = true;
                }
            }
            if (!hasLinkedFields) {
                await Api.fieldDelete(pointer);
            }
        }
    }

}

const FieldPromote = new FieldPromoteInstance();

export default FieldPromote;